import { AnyAction } from 'redux';
import { App, Produkt, Produktlinie, TkvVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { produktReducer } from '@ps20-frontend/reducers';
import { Heilbehandlungsschutz, Produktlinienname, Tierart } from '@ps20/bff-models';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { TkvActions } from '../tkvActions';
import { IAppState } from '../appState';

export const HEILBEHANDLUNGSSCHUTZ = 'HB';

export interface HeilbehandlungsschutzItem {
  id: Heilbehandlungsschutz;
  disabled?: boolean;
}

const shouldVorbelegenHeilbehandlungsschutz = (
  produktName: Produktlinienname,
  isPferd: boolean,
): boolean =>
  (isPferd && produktName === Produktlinienname.BASIS) ||
  (produktName !== Produktlinienname.BASIS && produktName !== Produktlinienname.PREMIUM);

export const tkvProduktReducer = function (
  sectionState: Produkt = null,
  action: AnyAction,
  state: IAppState,
): Produkt {
  const newSectionState = produktReducer(sectionState, action, App.TKV, state?.angebotsId);
  switch (action.type) {
    case TkvActions.HEILBEHANDLUNGSSCHUTZ_CHANGED: {
      const produktlinien: Produktlinie[] = newSectionState.produktlinien;
      if (
        produktlinien?.some(
          linie =>
            linie.selected &&
            linie.zusatzoptionen.some(option => option.name === HEILBEHANDLUNGSSCHUTZ),
        )
      ) {
        produktlinien
          .find(linie => linie.selected)
          .zusatzoptionen.find(option => option.name === HEILBEHANDLUNGSSCHUTZ).eigenschaft =
          action.payload;

        return { ...newSectionState, produktlinien };
      }
      return newSectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const produktlinien: Produktlinie[] = newSectionState.produktlinien;
      const actionPayload: TkvVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.TKV && actionPayload.angebotsId === state.angebotsId) {
        if (actionPayload?.produktlinienname) {
          const prodLinieFound = produktlinien.find(produktlinie => produktlinie.selected === true);
            const zusatzOption = prodLinieFound.zusatzoptionen.find(
            zusatzOption => zusatzOption.name === HEILBEHANDLUNGSSCHUTZ,
          );
            if (zusatzOption) {
              zusatzOption.eigenschaft = mapSublimitToEnum(
                actionPayload.tierart,
                actionPayload.sublimit,
              );
            }
        }
        return { ...newSectionState, produktlinien };
      }
      return newSectionState;
    }
    case TkvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT: {
      const tkvVorbelegungdaten: TkvVorbelegungdaten = action.payload;
      const newProduktLinien = newSectionState?.produktlinien?.map(produktlinie => {
        produktlinie.zusatzoptionen?.forEach(zusatzoption => {
          const vorhandeneVorbelegungdatenFound =
            !!tkvVorbelegungdaten?.vorvertragBausteinList.find(
              data => data?.taasTarifrisikoschluessel === zusatzoption?.name,
            );
          if (vorhandeneVorbelegungdatenFound) {
            zusatzoption.isSelected = true;
            // Heilbehandlungsschutz für Pferde bei BASIS immer vorbelegen, um den Wert bei ZUM_ANGEBOT_BUTTON_CLICKED prüfen zu können
            // BASIS für Hund und Katze sowie PREMIUM für alle Tierarten nicht vorbelegen, da BASIS nur 2000 und PREMIUM nur 5000 haben kann
            if (
              zusatzoption.name === HEILBEHANDLUNGSSCHUTZ &&
              shouldVorbelegenHeilbehandlungsschutz(
                produktlinie.produktName,
                produktTierartIsPferd(newSectionState),
              )
            ) {
              zusatzoption.eigenschaft = mapSublimitToEnum(
                tkvVorbelegungdaten.tierart,
                tkvVorbelegungdaten.sublimit,
              );
            }
          }
          return zusatzoption;
        });
        return produktlinie;
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    // Heilbehandlungsschutz für Pferde bei BASIS deaktivieren und für alle anderen Linien mit 5000 vorbelegen
    // Für alle neuen Angebote und gespeicherten Vorgänge oder Vorverträge, bei denen nicht 2000 gewählt wurde
    case TkvActions.ZUM_ANGEBOT_BUTTON_CLICKED: {
      if (produktTierartIsPferd(state.produkt)) {
        newSectionState.produktlinien.forEach(produktlinie =>
          produktlinie.zusatzoptionen.forEach(zusatzoption => {
            if (
              zusatzoption.name === HEILBEHANDLUNGSSCHUTZ &&
              (!state.vertragsoptionen.vorvertragLinie ||
                zusatzoption.eigenschaft !== Heilbehandlungsschutz.BIS_2000_100)
            ) {
              if (produktlinie.produktName === Produktlinienname.BASIS) {
                zusatzoption.disabled = true;
                zusatzoption.isSelected = false;
              } else {
                zusatzoption.eigenschaft = Heilbehandlungsschutz.BIS_5000_100;
              }
            }
          }),
        );
      }
      return newSectionState;
    }
    case TkvActions.PRODUKTLINIEN_CHANGED: {
      const produktlinien: Produktlinie[] = action.payload;
      return { ...newSectionState, produktlinien };
    }
    default:
      return newSectionState;
  }
};

export function mapSublimitToEnum(tierart: Tierart, eigenschaft: number): string {
  switch (eigenschaft) {
    case 2000:
      return tierart === Tierart.VRPFERD
        ? Heilbehandlungsschutz.BIS_2000_100
        : Heilbehandlungsschutz.BIS_2000_50;
    case 5000:
      return Heilbehandlungsschutz.BIS_5000_100;
  }
}

export function produktTierartIsPferd(produkt: Produkt): boolean {
  return (
    produkt?.produktlinien?.find(
      produktlinie => produktlinie.produktName === Produktlinienname.BASIS,
    )?.kurztext === 'TM005'
  );
}
